// Splits a suggestion into the part the user has already typed and the part it is adding.
//
// The un-typed part is what gets emphasised, which is the convention every search box has
// trained people on: the eye should land on what the suggestion contributes, not on the
// characters they just pressed.
//
// Matching is per word and prefix-based because that is how the suggestions index matches —
// `phrase_prefix` with operator `and` over edge n-grams. Every typed term is therefore a
// prefix of some word in the phrase, but not necessarily in the order typed, so "partners"
// legitimately matches the second word of "experience partners".

export interface SuggestionSegment {
  text: string;
  typed: boolean;
}

export const queryTerms = (query: string): string[] => (
  query.toLowerCase().split(/\s+/).filter((term) => term.length > 0)
);

// The longest matching term wins so that the whole of what the user accounted for reads as
// typed: against "partners", the query "part partners" should leave nothing emphasised.
const matchedPrefixLength = (word: string, terms: string[]): number => {
  const lower = word.toLowerCase();

  return terms.reduce((longest, term) => (
    lower.startsWith(term) && term.length > longest ? term.length : longest
  ), 0);
};

// Runs of equal emphasis are merged so the markup is one span per visible change, not one
// per character.
const coalesce = (phrase: string, typedMask: boolean[]): SuggestionSegment[] => {
  const segments: SuggestionSegment[] = [];
  let start = 0;

  for (let i = 1; i <= phrase.length; i += 1) {
    if (i === phrase.length || typedMask[i] !== typedMask[start]) {
      segments.push({ text: phrase.slice(start, i), typed: typedMask[start] });
      start = i;
    }
  }

  return segments;
};

export const highlightSegments = (phrase: string, query: string): SuggestionSegment[] => {
  if (!phrase) { return []; }

  const terms = queryTerms(query);
  if (!terms.length) { return [{ text: phrase, typed: false }]; }

  const typedMask: boolean[] = new Array(phrase.length).fill(false);
  // Built per call: a shared /g/ regex would carry lastIndex between suggestions.
  const word = /\S+/g;
  let match = word.exec(phrase);

  while (match !== null) {
    const length = matchedPrefixLength(match[0], terms);
    for (let i = 0; i < length; i += 1) {
      typedMask[match.index + i] = true;
    }
    match = word.exec(phrase);
  }

  // Emphasis belongs on words rather than on the gaps between them; bolding a space only
  // shifts the following word by a fraction as the user types.
  for (let i = 0; i < phrase.length; i += 1) {
    if (/\s/.test(phrase[i])) { typedMask[i] = true; }
  }

  return coalesce(phrase, typedMask);
};
